import { Button, Checkbox, Divider, Flex, Space, Spacing, Text } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBankAccountByBookkeepingAccountId } from '@features/bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'
import { BookkeepingItemType } from '@features/reconciliation/enums/bookkeepingItemType'

import { useDebouncedCallback } from '../../../../../../../../../../../../../hooks/useDebouncedCallback'
import { useBookkeepingItemsContext } from '../../../../../../../../../contexts/bookkeepingItemsContext'
import { useSandboxBankTransactionsFilters } from '../../../../../../../../../hooks/useSandboxBankTransactionsFilters'
import { AmountRangeFilter } from '../../../../../AmountRangeFilter/AmountRangeFilter'
import { SideFilter } from '../../../../../SideFilter'
import * as Styled from './styles'

const DEBOUNCE_DELAY = 500

enum FilterType {
  Invoices = 'invoices',
  Bills = 'bills',
  Posting = 'posting',
}

const filterTypeToBookkeepingTypes: Record<FilterType, BookkeepingItemType[]> = {
  [FilterType.Invoices]: [BookkeepingItemType.Invoice, BookkeepingItemType.ExternalInvoice],
  [FilterType.Bills]: [BookkeepingItemType.Bill],
  [FilterType.Posting]: [BookkeepingItemType.Posting],
}

const filterTypeLabels: Record<FilterType, string> = {
  [FilterType.Invoices]: 'bank_transactions.drawer.filters.type_invoice',
  [FilterType.Bills]: 'bank_transactions.drawer.filters.type_bill',
  [FilterType.Posting]: 'bank_transactions.drawer.filters.type_payment',
}

export const BookkeepingItemsFiltersPanel = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, setFilters, resetFilters } = useBookkeepingItemsContext()
  const [bankTransactionsFilters] = useSandboxBankTransactionsFilters()
  const { currency } = useBankAccountByBookkeepingAccountId(bankTransactionsFilters.accountId)

  const selectedTypes = useMemo(() => filters.types || [], [filters.types])

  const isFilterTypeSelected = useCallback(
    (filterType: FilterType): boolean => {
      const bookkeepingTypes = filterTypeToBookkeepingTypes[filterType]
      return bookkeepingTypes.every((type) => selectedTypes.includes(type))
    },
    [selectedTypes],
  )

  const handleTypeChange = useCallback(
    (filterType: FilterType) => (event: ChangeEvent<HTMLInputElement>) => {
      const bookkeepingTypes = filterTypeToBookkeepingTypes[filterType]
      const currentTypes = new Set(filters.types || [])

      if (event.target.checked) {
        for (const type of bookkeepingTypes) {
          currentTypes.add(type)
        }
      } else {
        for (const type of bookkeepingTypes) {
          currentTypes.delete(type)
        }
      }

      setFilters({ types: currentTypes.size > 0 ? Array.from(currentTypes) : undefined })
    },
    [filters.types, setFilters],
  )

  const handleClearAll = useCallback(() => {
    resetFilters()
  }, [resetFilters])

  const handleTransactionSideChange = useCallback(
    (value: TransactionTypeFilter) => {
      setFilters({ transactionSideFilter: value })
    },
    [setFilters],
  )

  const handleMinAmountChange = useDebouncedCallback((value: number) => {
    setFilters({ minAmount: value || undefined })
  }, DEBOUNCE_DELAY)

  const handleMaxAmountChange = useDebouncedCallback((value: number) => {
    setFilters({ maxAmount: value || undefined })
  }, DEBOUNCE_DELAY)

  return (
    <Styled.Panel>
      <Text colorVariant="discreet" weight="semiBold">
        {t('bank_transactions.drawer.filters.item_type')}
      </Text>
      <Flex flexDirection="column" gap={Spacing.S16}>
        {Object.values(FilterType).map((filterType) => (
          <Checkbox key={filterType} checked={isFilterTypeSelected(filterType)} onChange={handleTypeChange(filterType)}>
            {t(filterTypeLabels[filterType])}
          </Checkbox>
        ))}
      </Flex>
      <Space size="s8" />
      <SideFilter value={filters.transactionSideFilter} onChange={handleTransactionSideChange} />
      <Space size="s8" />
      <AmountRangeFilter
        minAmount={filters.minAmount}
        maxAmount={filters.maxAmount}
        currency={currency}
        onMinChange={handleMinAmountChange}
        onMaxChange={handleMaxAmountChange}
      />
      <Styled.FullWidthDivider>
        <Divider />
      </Styled.FullWidthDivider>
      <Flex justifyContent="flex-start">
        <Button variant="text" size="sm" icon="x" onClick={handleClearAll}>
          {t('bank_transactions.filter.clear_all')}
        </Button>
      </Flex>
    </Styled.Panel>
  )
}
